import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    if (this.hasAttribute('initialized')) return;
    this.setAttribute('initialized', '');
    this.#applyURLFiltersOnLoad();
  }

  /**
   * On metaobject pages the URL differs from the collection URL, so re-render
   * from the collection URL to populate filter options. Active filter/sort params
   * from the current URL are merged in so deep-linked states are preserved.
   */
  #applyURLFiltersOnLoad() {
    const resultsUrl = this.getAttribute('results-url');
    if (!resultsUrl) return;

    const resultsBase = new URL(resultsUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);

    if (resultsBase.pathname === currentUrl.pathname) return;

    const renderUrl = new URL(this.pageBaseURL);
    this.dispatchEvent(new FilterUpdateEvent(currentUrl.searchParams));
    sectionRenderer.renderSection(this.sectionId, { url: renderUrl }).catch(() => {
      this.removeAttribute('data-filters-pending');
    });
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles pagination control clicks without a full page reload.
   *
   * @param {Object<string, string>} data - URL search params from the pagination link
   * @param {Event} event
   */
  /**
   * Returns the base URL for building pagination fetch URLs. When a `results-url`
   * attribute is set and its path differs from the current page (e.g. a collection
   * URL embedded on a metaobject page), returns the collection URL with any active
   * filter/sort params merged in from the current URL.
   *
   * @returns {string}
   */
  get pageBaseURL() {
    const resultsUrl = this.getAttribute('results-url');
    if (!resultsUrl) return window.location.href;

    const resultsBase = new URL(resultsUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);

    if (resultsBase.pathname === currentUrl.pathname) return window.location.href;

    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (key.startsWith('filter.') || key === 'sort_by') {
        resultsBase.searchParams.append(key, value);
      }
    }
    return resultsBase.href;
  }

  /**
   * @param {Object<string, string>} data
   * @param {Event} event
   */
  async onPaginationControlClick(data, event) {
    event.preventDefault();
    const fetchURL = new URL(this.pageBaseURL);
    const historyUrl = new URL(window.location.href);
    for (const [key, value] of Object.entries(data)) {
      // Skip filter/sort params — pageBaseURL and historyUrl already carry the
      // correct multi-value state from window.location.href. Object.fromEntries
      // in parseData collapses duplicate keys so overwriting here would drop
      // any additional values the user selected for the same filter type.
      if (key.startsWith('filter.') || key === 'sort_by') continue;
      fetchURL.searchParams.set(key, value);
      historyUrl.searchParams.set(key, value);
    }
    history.pushState('', '', historyUrl.toString());
    await sectionRenderer.renderSection(this.sectionId, { url: fetchURL });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
